// The RBP Shopping List's programme header (RBP-D39). On the RBP page SL1.0 does not draw the
// depot shopping navigation (search, favourites, cart, orders, quick order; `parts/nav.php:47-48`);
// it draws the programme bar instead: a check mark, "Retailer Benefit Programme" ("RBP" on a
// tablet or phone, where the full name was cut off by the action, ADO-4037; the full name stays
// the accessible text) and the one action, "Add selected to cart" (`nav.php:149-163`, `:307-324`). SL1.0 puts
// "Quarter closing" in the page header on large screens and in the report header on the rest
// (`nav.php:344-347`, `rbp_report_header.htm:1-8`); here it is drawn once, on every layout. The
// logo returns to the depot chooser (`defs.php:533-541`).
//
// The bar is `ChooserHeader`'s (the logo, the User Menu, the brand theming) without the HHT
// link. "Add selected to cart" adds every selection when the page passes `onAddSelected`
// (`nav.php:307-324`), and is `aria-busy` while the add runs. Without a handler it holds its place
// disabled (`aria-disabled`, still focusable) with its reason as visually hidden text the button
// names as its description (`aria-describedby`), so a keyboard or screen-reader user hears why;
// the `title` repeats it for a pointer hover.
import { useId, useState } from 'react';
import { Link } from 'react-router';
import { formatDisplayDay } from '../../design-system/DatePicker/calendar';
import { Icon, UserMenu, useHeaderLayout } from '../../design-system';
import { useSessionStore } from '../../auth/useSessionStore';
import { CHOOSE_ROUTE } from '../../ordering/orderingChoices';
import { useHeaderUserMenu } from '../Products/useHeaderChrome';
import styles from './RbpProgrammeHeader.module.css';

export const ADD_SELECTED_UNAVAILABLE_REASON = 'Not available yet';

export function RbpProgrammeHeader({
  quarterClosesOn,
  onAddSelected,
  addBusy = false,
}: {
  /** `YYYY-MM-DD`, or null when no close date is set (or no report is current). */
  readonly quarterClosesOn: string | null;
  /** Adds every selection to the carts; without it the action is drawn disabled. */
  readonly onAddSelected?: () => void;
  /** An add is being prepared or made: the action is busy and a press starts nothing. */
  readonly addBusy?: boolean;
}) {
  const brand = useSessionStore((s) => s.brandConfig);
  const layout = useHeaderLayout();
  const mobile = layout === 'mobile';
  const shortTitle = layout !== 'desktop';
  const userMenu = useHeaderUserMenu();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const unavailableId = useId();

  return (
    <header className={styles.header} data-themed={brand.themed ? 'true' : undefined}>
      <div className={styles.bar}>
        <Link to={CHOOSE_ROUTE} className={styles.logo}>
          <img src={brand.logoUrl} alt={brand.name} className={styles.logoImg} />
        </Link>
        <div className={styles.programme}>
          <span className={styles.mark} aria-hidden>
            <Icon name="checkCircle" size={mobile ? 18 : 22} />
          </span>
          {shortTitle ? (
            <span className={styles.title}>
              <span aria-hidden="true">RBP</span>
              <span className={styles.visuallyHidden}>Retailer Benefit Programme</span>
            </span>
          ) : (
            <span className={styles.title}>Retailer Benefit Programme</span>
          )}
          {onAddSelected === undefined ? (
            <>
              <button
                type="button"
                className={styles.addSelected}
                aria-disabled="true"
                aria-describedby={unavailableId}
                title={ADD_SELECTED_UNAVAILABLE_REASON}
                onClick={(event) => event.preventDefault()}
              >
                Add selected to cart
              </button>
              <span id={unavailableId} className={styles.visuallyHidden}>
                {ADD_SELECTED_UNAVAILABLE_REASON}
              </span>
            </>
          ) : (
            <button
              type="button"
              className={styles.addSelected}
              aria-busy={addBusy}
              onClick={() => {
                if (!addBusy) onAddSelected();
              }}
            >
              Add selected to cart
            </button>
          )}
        </div>
        <div className={styles.end}>
          <UserMenu
            className={styles.userMenu}
            layout={layout}
            user={userMenu.user}
            account={userMenu.account}
            onSignOut={userMenu.onSignOut}
            themed={brand.themed}
            open={userMenuOpen}
            onOpenChange={setUserMenuOpen}
          />
        </div>
      </div>
      <p className={styles.closing}>
        <Icon name="calendar" size={16} />
        <span>
          Quarter closing:{' '}
          <strong>{quarterClosesOn === null ? '' : formatDisplayDay(quarterClosesOn)}</strong>
        </span>
      </p>
    </header>
  );
}
